import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  Typography,
  Button,
  FormControl,
  TextField,
  RadioGroup,
  Radio,
  FormLabel,
  Switch,
  Select,
  MenuItem,
} from "@mui/material";
import { Grid } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { changeFfd } from "API/swesum";
import dayjs from "dayjs";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import DateWithClear from "Components/UI/Form/DateWithClear";

const style = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: 4,
  padding: 4,
  width: "100&",
  height: "100%",
};

export default function DueDateModal({ invoice, open, setInvoice, onClose }) {
  const [dueDate, setDueDate] = useState(null);
  const [debitClient, setDebitClient] = useState(false);
  const [debitClientExtraFee, setDebitClientExtraFee] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [creditFee, setCreditFee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reminderAccounts, setReminderAccounts] = useState([]);
  const [creditFeeOptions, setCreditFeeOptions] = useState([
    {
      FfdLevel: invoice?.Client?.FfdLevel1,
      FFdFee: invoice?.Client?.Ffd1CreditFee,
    },
    {
      FfdLevel: invoice?.Client?.FfdLevel2,
      FFdFee: invoice?.Client?.Ffd2CreditFee,
    },
    {
      FfdLevel: invoice?.Client?.FfdLevel3,
      FFdFee: invoice?.Client?.Ffd3CreditFee,
    },
  ]);

  const { openSuccess, openError } = usePopupNotificationContext();

  const updateReminderAccounts = async () => {
    const payruleConditions = [];

    payruleConditions.push("PÅMINNELSE1");
    payruleConditions.push("PÅMINNELSE2");

    const reminderArticles = invoice?.EndCustomerArticles.filter((article) =>
      payruleConditions.includes(article.Payrule)
    );

    const reminderAccountsToWriteOff = invoice?.EndCustomerAccounts.filter(
      (account) => {
        const relatedArticle = reminderArticles.find(
          (article) =>
            article.Id === account.EndCustomerArticleId &&
            account.SystemStatus === "1" &&
            account.WriteOffId == null
        );
        if (relatedArticle) {
          account.label =
            relatedArticle.Payrule === "PÅMINNELSE1"
              ? "Påminnelse 1"
              : "Påminnelse 2";
          return true;
        }
        return false;
      }
    );

    setReminderAccounts(reminderAccountsToWriteOff);
  };

  const handleConfirm = async () => {
    setLoading(true);
    const now = dayjs();
    const date = dayjs(dueDate);

    setError("");
    if (date.isBefore(now, "day")) {
      setError("Valt datum får inte vara tidigare än idag.");
      setLoading(false);
      return;
    }

    const body = {
      date: dayjs(date).format("YYYY-MM-DD"),
      debitClient: creditFee ? true : debitClient,
      reason: reason,
      creditFee: creditFee ? Number(creditFeeOptions[creditFee].FFdFee) : null,
    };

    try {
      const res = await changeFfd(invoice?.Id, body);

      if (res) {
        setInvoice({
          ...invoice,
          ...res.data.Data,
        });

        openSuccess(res.data.Message);
      }
    } catch (error) {
      openError("Något gick fel.");
      setError(error);
    } finally {
      setLoading(false);
    }

    onClose();
  };

  useEffect(() => {
    if (invoice) {
      const defaultDate = dayjs(
        invoice?.CalcFfdDate ?? invoice?.OriginalFfdDate
      );
      setDueDate(defaultDate);
      setReason("");
      setError(null);
      setLoading(false);
      setDebitClient(false);
      setDebitClientExtraFee(false);
      setCreditFee(null);
      setCreditFeeOptions([
        {
          FfdLevel: invoice?.Client?.FfdLevel1,
          FFdFee: invoice?.Client?.Ffd1CreditFee,
        },
        {
          FfdLevel: invoice?.Client?.FfdLevel2,
          FFdFee: invoice?.Client?.Ffd2CreditFee,
        },
        {
          FfdLevel: invoice?.Client?.FfdLevel3,
          FFdFee: invoice?.Client?.Ffd3CreditFee,
        },
      ]);
      updateReminderAccounts();
    }
  }, [open]);

  return (
    <>
      <Dialog open={open}>
        <Box sx={style}>
          <Typography variant="h5" fontWeight={"bold"}>
            Justera Förfallodatum
          </Typography>
          <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            width={"100%"}
          >
            <FormControl fullWidth>
              <Box display="flex" gap={2} flexDirection="column">
                <Box
                  display="flex"
                  justifyContent={"center"}
                  alignItems="center"
                >
                  <Typography fontWeight={"medium"} fontSize={16} width={"50%"}>
                    Nytt förfallodatum:
                  </Typography>
                  <DateWithClear
                    onChange={(value) => setDueDate(value)}
                    value={dueDate}
                    slotProps={{
                      field: { clearable: true },
                      popper: {
                        disablePortal: false,
                      },
                    }}
                  />
                </Box>
                {reminderAccounts.length > 0 ? (
                  <Box display="flex" flexDirection={"column"} gap={2}>
                    <Typography fontWeight={"medium"} fontSize={16}>
                      Följande avgfiter kommer att skrivas av:
                    </Typography>
                    <Box>
                      {reminderAccounts.map((account) => {
                        return (
                          <Box
                            key={account.Id}
                            display="flex"
                            flexDirection="row"
                            marginBottom={2}
                            gap={0}
                          >
                            {" "}
                            <Typography
                              fontWeight={"medium"}
                              fontSize={14}
                              width={"50%"}
                            >
                              {account.label}
                            </Typography>
                            <Typography
                              fontSize={14}
                            >{`${account.Amount}:-`}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    <Typography
                      fontWeight={"medium"}
                      fontSize={16}
                      width={"50%"}
                    >
                      Debiteras avgifter:
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        defaultValue={false}
                        onChange={(e) => setDebitClient(e.target.value)}
                      >
                        <MenuItem value={true}>Klient</MenuItem>
                        <MenuItem value={false}>Swesum</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                ) : null}
                {invoice?.Service === "purchase" && (
                  <Box>
                    <Box display="flex" alignItems="center" gap={0}>
                      <Typography
                        fontWeight={"medium"}
                        fontSize={16}
                        width={"60%"}
                      >
                        Debitera klient extra kreditavgift:
                      </Typography>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="50%"
                      >
                        <Switch
                          defaultChecked={false}
                          onChange={(e) =>
                            setDebitClientExtraFee(e.target.checked ? 1 : 0)
                          }
                          color="primary"
                        />
                      </Box>
                    </Box>
                    {debitClientExtraFee === 1 ? (
                      <FormControl sx={{ maxWidth: "400px", width: "60%" }}>
                        <Grid container sx={{ maxWidth: "230px", mb: 1 }}>
                          <Grid item xs={6}>
                            <FormLabel component="legend">Dagar</FormLabel>
                          </Grid>
                          <Grid item xs={6}>
                            <FormLabel component="legend">Avgift</FormLabel>
                          </Grid>
                        </Grid>

                        <RadioGroup
                          onChange={(e) => setCreditFee(e.target.value)}
                        >
                          {creditFeeOptions.map((option, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                mb: 0,
                                position: "relative",
                                height: "40px",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  width: "calc(100% - 40px)",
                                }}
                              >
                                <Typography sx={{ width: "50%" }}>
                                  {option.FfdLevel}
                                </Typography>
                                <Typography sx={{ width: "50%" }}>
                                  {`${Number(option.FFdFee)}%`}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  position: "absolute",
                                  right: 0,
                                  width: "40px",
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <Radio value={index} />
                              </Box>
                            </Box>
                          ))}
                        </RadioGroup>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent={"flex-start"}
                          flexDirection="row"
                          marginTop={2}
                        >
                          <Typography
                            fontWeight={"medium"}
                            fontSize={16}
                            marginRight={1}
                          >
                            Extra kreditavgift:
                          </Typography>
                          <Typography fontSize={16}>
                            {`${
                              creditFee
                                ? Number(creditFeeOptions[creditFee]?.FFdFee) +
                                  "%"
                                : ""
                            }`}
                          </Typography>
                        </Box>
                      </FormControl>
                    ) : null}
                  </Box>
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  gap={2}
                >
                  <Typography
                    fontWeight={"medium"}
                    fontSize={16}
                    width={"100%"}
                  >
                    Notering:
                  </Typography>
                  <TextField
                    minRows={4}
                    placeholder="Notering"
                    onChange={(e) => setReason(e.target.value)}
                    multiline
                    style={{
                      minWidth: 450,
                      width: "100%",
                      borderRadius: 3,
                      border: "1px solid gray",
                    }}
                  />
                </Box>
              </Box>
              {error ? (
                <Box>
                  <Typography color={"error"}>{error}</Typography>
                </Box>
              ) : null}
            </FormControl>
          </Box>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={5}
            justifyContent={"space-around"}
            height={55}
            width={"100%"}
          >
            <Button
              sx={{
                backgroundColor: "transparent",
                outline: "1px solid #1976d2",
                color: "#1976d2",
                width: "100%",
              }}
              onClick={() => onClose()}
            >
              Avbryt
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleConfirm}
              loading={loading}
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                width: "100%",
              }}
            >
              Bekräfta
            </LoadingButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
